import {
  DurationPickerV2 as DurationPicker,
  InfoIconPopover,
  InfoPopover,
  NumberField,
  Typography,
  castTimeBreakdownStrings,
  getTimeBreakdownStrings,
  getTotalSeconds,
} from '@automata/ui';
import { logger } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import { useMemo } from 'react';

import SlotField from './SlotField';
import { LABWARE_ITEM_ALLOWLIST } from '../../../../errorAllowOrBlockLists';
import { selectWorkflowLevelErrorsForLabwareId } from '../../../../features/workflowJsonSelectors/workflowLevelErrors';
import { useAppSelector } from '../../../../hooks';
import type { InstrumentForLocation } from '../../../../types';
import { instrumentIsValidStartingLocation } from '../../../../utils';
import {
  type LabwarePropertiesForm,
  castLabwareFieldWithFallback,
} from '../../../../zodSchemas/labwareProperties';
import { BaseLabwareSettings } from '../BaseLabwareSettings';
import { ClosureStateSelect } from '../ClosureStateSelect';
import { ContentField } from '../ContentField';
import { FormSectionLabel } from '../FormSectionLabel';
import { InstrumentSelect } from '../InstrumentSelect';

// NOTE(IL, 2025-02-11): Use handlePendingChange for text fields with incomplete values,
// and handleSubmitForm(override) for checkboxes and dropdowns.
export type LabwareFieldsProps = {
  handlePendingChange: (update: Partial<LabwarePropertiesForm>) => void;
  handleSubmitForm: (override?: Partial<LabwarePropertiesForm>) => void;
  instruments: InstrumentForLocation[];
  labwareColorIndex: number;
  labwareId: string;
  setLabwareColor: (colorIndex: number) => void;
  values: LabwarePropertiesForm;
  workflowLabwareDefinitionURIs: string[];
};

export const LabwareFields = (props: LabwareFieldsProps) => {
  const {
    handlePendingChange,
    handleSubmitForm,
    instruments,
    labwareColorIndex,
    labwareId,
    setLabwareColor,
    values,
    workflowLabwareDefinitionURIs,
  } = props;
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );

  const errors = useAppSelector((state) =>
    selectWorkflowLevelErrorsForLabwareId(state, labwareId)
  ).filter((err) => LABWARE_ITEM_ALLOWLIST.includes(err.error.message));

  const startLocationInstruments = useMemo(
    () => instruments.filter(instrumentIsValidStartingLocation),
    [instruments]
  );

  const startInstrument = useMemo(
    () =>
      instruments.find((instrument) => instrument.id === values.startLocation),
    [instruments, values.startLocation]
  );

  // cast form values to stricter types when needed
  const { castingFailed: labwareTypeCastingFailed, value: labwareType } =
    castLabwareFieldWithFallback(values, 'labwareType', 'microplate');
  const { castingFailed: contentCastingFailed, value: content } =
    castLabwareFieldWithFallback(values, 'content', 'empty');
  const { castingFailed: closureStateCastingFailed, value: closureState } =
    castLabwareFieldWithFallback(values, 'closureState', 'open');

  if (
    labwareTypeCastingFailed ||
    contentCastingFailed ||
    closureStateCastingFailed
  ) {
    logger.error(
      `Labware ${labwareId} has invalid required values, could not cast.`,
      values
    );
  }

  // visibility booleans
  const showContentField = labwareType !== 'tiprack';
  const showSlotField =
    startInstrument &&
    startInstrument.capacity > 1 &&
    !startInstrument.is_slotless_container;
  const showTotalUnitsField = values.singleOrMultiUse === 'multi';
  const maxIdleTimeBreakdown = getTimeBreakdownStrings(values.maxIdleTime);

  return (
    <Stack gap={1} onBlur={() => handleSubmitForm()}>
      <BaseLabwareSettings
        handlePendingChange={handlePendingChange}
        handleSubmitForm={handleSubmitForm}
        labwareColorIndex={labwareColorIndex}
        labwareType={labwareType}
        setLabwareColor={setLabwareColor}
        labwareDefinitionURI={values.labwareDefinitionURI ?? ''}
        nickname={values.nickname}
        workflowLabwareDefinitionURIs={workflowLabwareDefinitionURIs}
      />

      <Box mt={2}>
        <FormSectionLabel>Settings</FormSectionLabel>
      </Box>

      {showContentField ? (
        <ContentField
          content={content}
          contentDescription={values.contentDescription}
          updateFields={handlePendingChange}
        />
      ) : null}

      <ClosureStateSelect
        labwareType={labwareType}
        value={closureState}
        onChange={(value) => handleSubmitForm({ closureState: value })}
      />

      <InstrumentSelect
        onChange={(value) => {
          handleSubmitForm({ startLocation: value ?? undefined });
        }}
        label="Start Location"
        value={values.startLocation}
        instruments={startLocationInstruments}
      />
      {showSlotField ? (
        <SlotField
          id={labwareId}
          value={values.slot ?? ''}
          onChange={(value) => handleSubmitForm({ slot: value })}
          errors={workflowLevelErrorsVisible ? errors : []}
        />
      ) : null}
      <FormControlLabel
        label={
          <>
            Reusable across batches
            <InfoIconPopover
              infoPopover={
                <InfoPopover
                  title="Reusable across batches"
                  copy="Define whether the labware can be reused across multiple batches. Disable this option to restrict its use to a single batch, with fresh labware required for each new one."
                  example="An assay plate is typically single-batch use, while a reservoir can hold enough reagent to be reused across multiple batches."
                />
              }
              infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              infoPopoverTransformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            />
          </>
        }
        control={
          <Checkbox
            checked={values.singleOrMultiUse === 'multi'}
            onChange={(e) => {
              handleSubmitForm({
                singleOrMultiUse: e.target.checked ? 'multi' : 'single',
              });
            }}
          />
        }
      />
      {showTotalUnitsField && (
        <NumberField
          infoPopover={
            <InfoPopover
              title="Total units"
              copy="Set how many units this labware contains. Units are used to track consumption per task and determine when a replacement labware is required."
            />
          }
          infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          infoPopoverTransformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          label="Total units"
          min={0}
          value={values.totalUnits}
          onChange={(value) => handlePendingChange({ totalUnits: value })}
        />
      )}
      <Typography variant="overline">
        Maximum idle time
        <InfoIconPopover
          infoPopover={
            <InfoPopover
              title="Maximum idle time"
              copy="This is the sum of the transport time and the time for the next action to occur. <br /><br /> The maximum idle time is none by default. Ticking this box, you will be able to set up a specific value for this labware."
            />
          }
          infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          infoPopoverTransformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        />
      </Typography>
      <FormControlLabel
        label="Set maximum idle time"
        control={
          <Checkbox
            checked={values.maxIdleTimeExists}
            onChange={(e) => {
              const maxIdleTimeExists = e.target.checked;
              handleSubmitForm({
                maxIdleTimeExists,
                maxIdleTime: maxIdleTimeExists ? values.maxIdleTime : null,
              });
            }}
          />
        }
      />
      {values.maxIdleTimeExists && (
        <DurationPicker
          hours={maxIdleTimeBreakdown.hours}
          minutes={maxIdleTimeBreakdown.minutes}
          seconds={maxIdleTimeBreakdown.seconds}
          handleUpdateTime={(timePatch) => {
            const maxIdleTime = getTotalSeconds(
              castTimeBreakdownStrings({
                ...maxIdleTimeBreakdown,
                ...timePatch,
              })
            );
            handlePendingChange({ maxIdleTime });
          }}
        />
      )}
    </Stack>
  );
};
